import { useState } from "react"

import { Box, ButtonBase, Popper, Stack, type StackProps, type Theme } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { last } from "lodash"

import { Link, Tag, type FC, type TagProps } from ".."

type TagsProps = {
  tags: TagProps[]
  autoWidth?: boolean
} & StackProps

const Tags: FC<TagsProps> = ({ tags = [], autoWidth = false, ...props }) => {
  const visibleTags = tags.slice(0, 3)
  const lastVisibleTag = last(visibleTags)
  const extraTags = tags.slice(3)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const open = Boolean(anchorEl)

  const fontColor = (color: string, theme: Theme) => {
    switch (color) {
      case "orange":
      case "floralWhite":
        return encodeURIComponent(theme.palette["primary.900"].main)

      case "blue":
        return encodeURIComponent(theme.palette["neutral.100"].main)

      case "white":
      default:
        return encodeURIComponent(theme.palette["neutral.700"].main)
    }
  }

  return (
    <>
      <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={2} {...props}>
        {visibleTags.map((tag, i) => (
          <Tag key={i} {...tag} autoWidth={autoWidth} />
        ))}
        {lastVisibleTag && extraTags.length > 0 && (
          <>
            <ButtonBase
              aria-label={`Open ${extraTags.length} more tags`}
              type="button"
              onClick={handleClick}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Tag
                {...lastVisibleTag}
                uri={undefined}
                label={`+${extraTags.length} more`}
                sx={{ textTransform: "none" }}
                autoWidth
              />
            </ButtonBase>
          </>
        )}
      </Stack>
      {lastVisibleTag && extraTags.length > 0 && (
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-end"
          disablePortal
          modifiers={[
            {
              name: "flip",
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: "document",
                padding: 8,
              },
            },
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                rootBoundary: "document",
                padding: 8,
              },
            },
            {
              name: "arrow",
              enabled: false,
            },
          ]}
          sx={{
            zIndex: 10,
          }}
        >
          <Box
            rowGap={3}
            minWidth={125}
            borderRadius={1.5}
            boxShadow={(theme) =>
              `1px 1px 10px 0px ${alpha(theme.palette["neutral.900"].main, 0.15)}`
            }
            mt={2.5}
            p={3}
            sx={[
              (theme) => {
                switch (lastVisibleTag.color) {
                  case "orange":
                    return {
                      bgcolor: theme.palette["primary.200"].main,
                      color: theme.palette["primary.900"].main,
                    }
                  case "blue":
                    return {
                      bgcolor: theme.palette["secondary.800"].main,
                      color: theme.palette["neutral.100"].main,
                    }
                  case "floralWhite":
                    return {
                      bgcolor: theme.palette["tertiary.200"].main,
                      color: theme.palette["neutral.700"].main,
                    }
                  case "white":
                  default:
                    return {
                      bgcolor: theme.palette["neutral.100"].main,
                      color: theme.palette["neutral.700"].main,
                    }
                }
              },
            ]}
          >
            <Stack
              rowGap={3}
              component="ul"
              sx={(theme) => ({
                "listStyle": "none",
                "listStylePosition": "inside",

                "li::before": {
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='8' viewBox='0 0 6 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.387618 0.513662L5.4353 3.82942C5.52157 3.88616 5.52157 4.11347 5.4353 4.17058L0.387618 7.48634C0.344713 7.51434 0.295842 7.4985 0.263032 7.44618L0.0335917 7.07776C9.34775e-05 7.02397 -0.00931359 6.94292 0.00972993 6.87366L0.792579 4L0.00972968 1.12634C0.00330536 1.10276 9.32381e-05 1.07771 9.3237e-05 1.05266C9.32349e-05 1.00476 0.0117947 0.957606 0.0335915 0.922238L0.263031 0.55382C0.295612 0.501504 0.344712 0.485663 0.387618 0.513662Z' fill='${fontColor(
                    lastVisibleTag.color ?? "white",
                    theme
                  )}'/%3E%3C/svg%3E%0A");`,
                  backgroundSize: "6px 8px",
                  backgroundRepeat: "no-repeat",
                  content: "''",
                  width: 10,
                  height: 8,
                  display: "block",
                  transition: theme.transitions.create("background-position"),
                },

                "li": {
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  columnGap: 1,
                },

                "li:hover::before": {
                  backgroundPosition: "4px center",
                },
              })}
            >
              {extraTags.map((tag, i) => (
                <Box component="li" key={i}>
                  <Link
                    type="base"
                    variant="link.xs"
                    href={tag.uri}
                    sx={[
                      (theme) => {
                        switch (lastVisibleTag.color) {
                          case "orange":
                            return {
                              color: theme.palette["primary.900"].main,
                            }
                          case "blue":
                            return {
                              color: theme.palette["neutral.100"].main,
                            }
                          case "floralWhite":
                          case "white":
                          default:
                            return {
                              color: theme.palette["neutral.700"].main,
                            }
                        }
                      },
                    ]}
                  >
                    {tag.label || tag.title}
                  </Link>
                </Box>
              ))}
            </Stack>
          </Box>
        </Popper>
      )}
    </>
  )
}
export default Tags
